'use client';

/**
 * CommandPalette Component
 *
 * A VS Code/Linear-style command palette with fuzzy search, keyboard navigation,
 * and categorized actions. Opens with Cmd+K (Mac) or Ctrl+K (Windows/Linux).
 *
 * Enhanced with:
 * - Two-column layout (search results + entity preview)
 * - Mode switching between search and chat (Tab key)
 * - Embedded AI chat for entity questions
 *
 * @see Issue #236 - Cross-Entity Global Search
 */

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import RecentIcon from '@mui/icons-material/History';
import EnterIcon from '@mui/icons-material/KeyboardReturn';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchIcon from '@mui/icons-material/Search';
import {
  alpha,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  InputAdornment,
  InputBase,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { AnimatePresence, motion } from 'framer-motion';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { glassEffects } from '../../theme/shadows';
import { iconSizes } from '../../theme/typography';
import { AiIcon } from '../Icons';
import { CommandPaletteChat } from './CommandPaletteChat';
import { CommandPalettePreview } from './CommandPalettePreview';
import type { CommandPaletteItemExtended, CommandPaletteMode, EntityPreviewData, PaletteChatMessage } from './types';

// ============================================================================
// Types
// ============================================================================

export interface CommandPaletteItem {
  /** Unique identifier */
  id: string;
  /** Display label */
  label: string;
  /** Optional description */
  description?: string;
  /** Category for grouping */
  category?: string;
  /** Icon to display */
  icon?: React.ReactNode;
  /** Keyboard shortcut hint */
  shortcut?: string[];
  /** Keywords for search matching */
  keywords?: string[];
  /** Action to execute */
  onSelect?: () => void;
  /** Whether the item is disabled */
  disabled?: boolean;
  /** Custom render function */
  render?: (item: CommandPaletteItem, isActive: boolean) => React.ReactNode;
}

export interface CommandPaletteCategory {
  /** Category identifier */
  id: string;
  /** Display label */
  label: string;
  /** Icon for the category */
  icon?: React.ReactNode;
  /** Priority for ordering (lower = higher priority) */
  priority?: number;
}

export interface CommandPaletteProps {
  /** Whether the palette is open */
  open: boolean;
  /** Callback when palette should close */
  onClose: () => void;
  /** Items to display */
  items: CommandPaletteItem[];
  /** Category definitions */
  categories?: CommandPaletteCategory[];
  /** Recent items (shown at top) */
  recentItems?: CommandPaletteItem[];
  /** Maximum recent items to show */
  maxRecentItems?: number;
  /** Placeholder text */
  placeholder?: string;
  /** Empty state message */
  emptyMessage?: string;
  /** No results message */
  noResultsMessage?: string;
  /** Enable fuzzy search */
  fuzzySearch?: boolean;
  /** Called when search query changes */
  onSearchChange?: (query: string) => void;
  /** Called when an item is selected */
  onItemSelect?: (item: CommandPaletteItem) => void;
  /** Custom footer content */
  footer?: React.ReactNode;
  /** Whether to show keyboard hints in footer */
  showKeyboardHints?: boolean;
  /** Initial search query */
  initialQuery?: string;
  /** Whether async search is in progress (Issue #236) */
  isSearching?: boolean;

  // ---- Enhanced Features (Issue #236) ----

  /** Enable preview panel (two-column layout) */
  enablePreview?: boolean;
  /** Preview panel width in pixels */
  previewWidth?: number;
  /** Preview data for the focused item */
  previewData?: EntityPreviewData | null;
  /** Whether preview is loading */
  isPreviewLoading?: boolean;
  /** Callback when item is focused (for preview) */
  onItemFocus?: (item: CommandPaletteItemExtended | null) => void;
  /** Callback to view full details from preview */
  onViewDetails?: (entity: EntityPreviewData) => void;
  /** Callback for quick action in preview */
  onPreviewAction?: (actionId: string, entity: EntityPreviewData) => void;

  /** Enable embedded chat */
  enableChat?: boolean;
  /** Current palette mode */
  mode?: CommandPaletteMode;
  /** Callback when mode changes */
  onModeChange?: (mode: CommandPaletteMode) => void;
  /** Chat messages */
  chatMessages?: PaletteChatMessage[];
  /** Whether chat is streaming */
  isChatStreaming?: boolean;
  /** Chat entity context */
  chatContext?: EntityPreviewData | null;
  /** Callback to send chat message */
  onChatSend?: (message: string) => void;
  /** Callback to clear chat context */
  onChatClearContext?: () => void;
  /** Callback to clear chat history */
  onChatClearHistory?: () => void;
  /** Callback to start chat about an entity from preview */
  onStartChat?: (entity: EntityPreviewData) => void;
}

export interface CommandPaletteRef {
  /** Focus the search input */
  focus: () => void;
  /** Clear the search query */
  clear: () => void;
  /** Set the search query */
  setQuery: (query: string) => void;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledDialogProps {
  $isDark: boolean;
  $enablePreview?: boolean;
  $previewWidth?: number;
}

const StyledDialog = styled(Dialog, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledDialogProps>(({ theme, $isDark, $enablePreview, $previewWidth = 320 }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  // Two-column layout: search (640) + preview (previewWidth)
  const maxWidth = $enablePreview ? 640 + $previewWidth : 640;

  return {
    '& .MuiDialog-paper': {
      position: 'fixed',
      top: '10%',
      margin: 0,
      width: '100%',
      maxWidth,
      maxHeight: '70vh',
      borderRadius: 16,
      background: $isDark ? 'rgba(13, 33, 55, 0.98)' : 'rgba(255, 255, 255, 0.98)',
      backdropFilter: glass.backdropBlur,
      border: `1px solid ${glass.border}`,
      boxShadow: `0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)}`,
      overflow: 'hidden',
      // Mobile: Full width, no preview
      [theme.breakpoints.down('md')]: {
        top: '5%',
        maxWidth: '95vw',
        maxHeight: '85vh',
        borderRadius: 12,
      },
    },

    '& .MuiBackdrop-root': {
      backgroundColor: $isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.4)',
      backdropFilter: 'blur(4px)',
    },
  };
});

/** Container for the two-column layout */
const TwoColumnLayout = styled(Box)({
  display: 'flex',
  height: '100%',
});

/** Left column for search/chat */
const LeftColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
});

/** Right column for preview (hidden on mobile) */
const RightColumn = styled(Box)(({ theme }) => ({
  borderLeft: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  // Hide preview on mobile/tablet
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  flex: 1,
  fontSize: '1rem',
  '& .MuiInputBase-input': {
    padding: theme.spacing(0.5, 1),
    '&::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 0.7,
    },
  },
}));

const ResultsContainer = styled(Box)({
  overflowY: 'auto',
  flex: 1,
  minHeight: 0,
});

const CategoryHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.02) : alpha(theme.palette.common.black, 0.02),
}));

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  margin: theme.spacing(0.5, 1),
  borderRadius: 8,
  padding: theme.spacing(1, 1.5),
  backgroundColor: isActive
    ? theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.15)
      : alpha(theme.palette.primary.main, 0.1)
    : 'transparent',
  border: isActive ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}` : '1px solid transparent',
  transition: 'all 100ms ease',

  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.primary.main, 0.08),
  },

  '&.Mui-disabled': {
    opacity: 0.5,
  },
}));

const ShortcutChip = styled(Chip)(({ theme }) => ({
  height: 22,
  fontSize: '0.75rem',
  fontFamily: 'monospace',
  fontWeight: 600,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.common.black, 0.06),
  border: `1px solid ${theme.palette.divider}`,
  '& .MuiChip-label': {
    padding: '0 6px',
  },
}));

const FooterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.02) : alpha(theme.palette.common.black, 0.02),
}));

const KeyboardHint = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
}));

const KeyboardKey = styled('kbd')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 20,
  height: 20,
  padding: '0 4px',
  fontSize: '0.6875rem',
  fontFamily: 'inherit',
  fontWeight: 600,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.common.black, 0.06),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 4,
  boxShadow: `0 1px 0 ${theme.palette.divider}`,
}));

/** Styled "Ask AI" item with gradient accent */
const AskAiListItemButton = styled(StyledListItemButton)(({ theme }) => ({
  background:
    theme.palette.mode === 'dark'
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`
      : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
  borderColor: alpha(theme.palette.primary.main, 0.2),
  '&:hover': {
    background:
      theme.palette.mode === 'dark'
        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)} 0%, ${alpha(theme.palette.secondary.main, 0.12)} 100%)`
        : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
  },
}));

/** Chat mode header with back button */
const ChatHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.02) : alpha(theme.palette.common.black, 0.02),
}));

// ============================================================================
// Constants
// ============================================================================

/** Special ID for the "Ask AI" item */
const ASK_AI_ITEM_ID = '__ask_ai__';

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Simple fuzzy search matching
 */
function fuzzyMatch(text: string, query: string): boolean {
  const textLower = text.toLowerCase();
  const queryLower = query.toLowerCase();

  let queryIndex = 0;
  for (let i = 0; i < textLower.length && queryIndex < queryLower.length; i++) {
    if (textLower[i] === queryLower[queryIndex]) {
      queryIndex++;
    }
  }
  return queryIndex === queryLower.length;
}

/**
 * Score a match (higher = better)
 */
function scoreMatch(item: CommandPaletteItem, query: string): number {
  const queryLower = query.toLowerCase();
  const labelLower = item.label.toLowerCase();

  // Exact match
  if (labelLower === queryLower) return 100;

  // Starts with query
  if (labelLower.startsWith(queryLower)) return 80;

  // Contains query as substring
  if (labelLower.includes(queryLower)) return 60;

  // Keyword match
  if (item.keywords?.some((k) => k.toLowerCase().includes(queryLower))) return 50;

  // Description match
  if (item.description?.toLowerCase().includes(queryLower)) return 30;

  // Fuzzy match
  if (fuzzyMatch(labelLower, queryLower)) return 20;

  return 0;
}

// ============================================================================
// Component
// ============================================================================

export const CommandPalette = forwardRef<CommandPaletteRef, CommandPaletteProps>(
  (
    {
      open,
      onClose,
      items,
      categories = [],
      recentItems = [],
      maxRecentItems = 5,
      placeholder,
      emptyMessage,
      noResultsMessage,
      fuzzySearch: _fuzzySearch = true,
      onSearchChange,
      onItemSelect,
      footer,
      showKeyboardHints = true,
      initialQuery = '',
      isSearching = false,
      // Enhanced features (Issue #236)
      enablePreview = false,
      previewWidth = 320,
      previewData = null,
      isPreviewLoading = false,
      onItemFocus,
      onViewDetails,
      onPreviewAction,
      enableChat = false,
      mode: controlledMode,
      onModeChange,
      chatMessages = [],
      isChatStreaming = false,
      chatContext = null,
      onChatSend,
      onChatClearContext,
      onChatClearHistory,
      onStartChat,
    },
    ref,
  ) => {
    const theme = useTheme();
    const { t } = useI18n();
    const isDark = theme.palette.mode === 'dark';
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    // Mode state (controlled or uncontrolled)
    const [internalMode, setInternalMode] = useState<CommandPaletteMode>('search');
    const currentMode = controlledMode ?? internalMode;
    const setMode = useCallback(
      (newMode: CommandPaletteMode) => {
        setInternalMode(newMode);
        onModeChange?.(newMode);
      },
      [onModeChange],
    );

    // Use i18n for default values
    const displayPlaceholder = placeholder ?? t('palette.placeholder');
    const displayEmptyMessage = emptyMessage ?? t('palette.empty');
    const displayNoResultsMessage = noResultsMessage ?? t('palette.noResults');

    const [query, setQuery] = useState(initialQuery);
    const [activeIndex, setActiveIndex] = useState(0);

    // Filter and sort items based on query
    const filteredItems = useMemo(() => {
      if (!query.trim()) {
        return items;
      }

      const scored = items
        .map((item) => ({
          item,
          score: scoreMatch(item, query),
        }))
        .filter(({ score }) => score > 0)
        .sort((a, b) => b.score - a.score);

      return scored.map(({ item }) => item);
    }, [items, query]);

    // Group items by category
    const groupedItems = useMemo(() => {
      const groups: Record<string, CommandPaletteItem[]> = {};
      const uncategorized: CommandPaletteItem[] = [];

      for (const item of filteredItems) {
        if (item.category) {
          if (!groups[item.category]) {
            groups[item.category] = [];
          }
          groups[item.category].push(item);
        } else {
          uncategorized.push(item);
        }
      }

      // Sort categories by priority
      const sortedCategories = [...categories].sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99));

      const result: { category: CommandPaletteCategory | null; items: CommandPaletteItem[] }[] = [];

      for (const cat of sortedCategories) {
        if (groups[cat.id]?.length) {
          result.push({ category: cat, items: groups[cat.id] });
        }
      }

      // Add uncategorized at the end
      if (uncategorized.length) {
        result.push({ category: null, items: uncategorized });
      }

      return result;
    }, [filteredItems, categories]);

    // Create the "Ask AI" item when chat is enabled
    const askAiItem: CommandPaletteItem | null = useMemo(() => {
      if (!enableChat) return null;
      const trimmedQuery = query.trim();
      return {
        id: ASK_AI_ITEM_ID,
        label: trimmedQuery ? t('palette.askAi', { query: trimmedQuery }) : t('palette.askAiEmpty'),
        description: t('palette.askAiDescription'),
        icon: <AiIcon />,
        category: '__ai__',
      };
    }, [enableChat, query, t]);

    // Flat list of all visible items for keyboard navigation
    const flatItems = useMemo(() => {
      const items: CommandPaletteItem[] = [];

      // Add recent items if no query
      if (!query.trim() && recentItems.length > 0) {
        items.push(...recentItems.slice(0, maxRecentItems));
      }

      // Add grouped items
      for (const group of groupedItems) {
        items.push(...group.items);
      }

      // Add "Ask AI" item at the end when chat is enabled
      if (askAiItem) {
        items.push(askAiItem);
      }

      return items;
    }, [groupedItems, recentItems, maxRecentItems, query, askAiItem]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
      clear: () => setQuery(''),
      setQuery: (q) => setQuery(q),
    }));

    // Reset state when opening
    useEffect(() => {
      if (open) {
        setQuery(initialQuery);
        setActiveIndex(0);
        setInternalMode('search');
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    }, [open, initialQuery]);

    // Reset active index when results change
    useEffect(() => {
      setActiveIndex(0);
    }, [flatItems.length]);

    // Scroll active item into view
    useEffect(() => {
      if (listRef.current) {
        const activeItem = listRef.current.querySelector('[data-active="true"]');
        if (activeItem) {
          activeItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }
    }, [activeIndex]);

    const handleQueryChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newQuery = e.target.value;
        setQuery(newQuery);
        onSearchChange?.(newQuery);
      },
      [onSearchChange],
    );

    const handleItemSelect = useCallback(
      (item: CommandPaletteItem) => {
        if (item.disabled) return;

        // Handle "Ask AI" item specially
        if (item.id === ASK_AI_ITEM_ID) {
          const trimmedQuery = query.trim();
          // Switch to chat mode
          setMode('chat');
          // If there's a query, send it as a chat message
          if (trimmedQuery && onChatSend) {
            // Small delay to allow mode switch animation
            setTimeout(() => {
              onChatSend(trimmedQuery);
            }, 50);
          }
          // Clear the search query since we're now in chat
          setQuery('');
          return;
        }

        onItemSelect?.(item);
        item.onSelect?.();
        onClose();
      },
      [onItemSelect, onClose, query, setMode, onChatSend],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        // In chat mode, handle navigation back to search
        if (currentMode === 'chat') {
          if (e.key === 'Escape') {
            e.preventDefault();
            // First press goes back to search, second closes
            setMode('search');
          }
          return;
        }

        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setActiveIndex((prev) => {
              const newIndex = (prev + 1) % flatItems.length;
              // Notify about focus change for preview
              if (onItemFocus && flatItems[newIndex]) {
                onItemFocus(flatItems[newIndex] as CommandPaletteItemExtended);
              }
              return newIndex;
            });
            break;
          case 'ArrowUp':
            e.preventDefault();
            setActiveIndex((prev) => {
              const newIndex = (prev - 1 + flatItems.length) % flatItems.length;
              // Notify about focus change for preview
              if (onItemFocus && flatItems[newIndex]) {
                onItemFocus(flatItems[newIndex] as CommandPaletteItemExtended);
              }
              return newIndex;
            });
            break;
          case 'Enter':
            e.preventDefault();
            if (flatItems[activeIndex]) {
              handleItemSelect(flatItems[activeIndex]);
            }
            break;
          case 'Escape':
            e.preventDefault();
            onClose();
            break;
        }
      },
      [flatItems, activeIndex, handleItemSelect, onClose, enableChat, currentMode, setMode, onItemFocus],
    );

    const renderItem = (item: CommandPaletteItem, _index: number, isRecent = false) => {
      const isActive = flatItems.indexOf(item) === activeIndex;

      if (item.render) {
        return item.render(item, isActive);
      }

      // Special rendering for "Ask AI" item
      const isAskAiItem = item.id === ASK_AI_ITEM_ID;
      const ListItemButtonComponent = isAskAiItem ? AskAiListItemButton : StyledListItemButton;

      return (
        <ListItem key={item.id} disablePadding>
          <ListItemButtonComponent
            isActive={isActive}
            disabled={item.disabled}
            onClick={() => handleItemSelect(item)}
            data-active={isActive}
          >
            {item.icon && (
              <ListItemIcon
                sx={{
                  minWidth: 36,
                  color: isAskAiItem ? 'primary.main' : isActive ? 'primary.main' : 'text.secondary',
                }}
              >
                {item.icon}
              </ListItemIcon>
            )}
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={[
                      {
                        fontWeight: isActive || isAskAiItem ? 600 : 400,
                      },
                      ...(isAskAiItem ? [{ color: 'primary.main' }] : []),
                    ]}
                  >
                    {item.label}
                  </Typography>
                  {isRecent && (
                    <Chip
                      size="small"
                      label={t('palette.recent')}
                      sx={{
                        height: 18,
                        fontSize: '0.75rem',
                        bgcolor: 'action.hover',
                      }}
                    />
                  )}
                </Box>
              }
              secondary={item.description}
              slotProps={{
                secondary: {
                  variant: 'caption',
                  sx: { opacity: 0.7 },
                },
              }}
            />
            {item.shortcut && item.shortcut.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {item.shortcut.map((key, i) => (
                  <ShortcutChip key={i} label={key} size="small" />
                ))}
              </Box>
            )}
            {isAskAiItem ? (
              <KeyboardKey sx={{ ml: 1 }}>
                <EnterIcon sx={{ fontSize: 10 /* Off-scale: keyboard hint icons, smaller than 2xs (12) */ }} />
              </KeyboardKey>
            ) : (
              <NavigateNextIcon sx={{ ml: 1, opacity: 0.5, fontSize: iconSizes.md }} />
            )}
          </ListItemButtonComponent>
        </ListItem>
      );
    };

    const showRecent = !query.trim() && recentItems.length > 0;
    const hasResults = flatItems.length > 0;

    // Handle preview quick action
    const handlePreviewQuickAction = useCallback(
      (actionId: string) => {
        if (previewData && onPreviewAction) {
          onPreviewAction(actionId, previewData);
        }
      },
      [previewData, onPreviewAction],
    );

    // Render search mode content
    const renderSearchContent = () => (
      <>
        {/* Search Input */}
        <SearchContainer>
          <InputAdornment position="start">
            <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
          </InputAdornment>
          <StyledInputBase
            inputRef={inputRef}
            value={query}
            onChange={handleQueryChange}
            placeholder={displayPlaceholder}
            fullWidth
            autoFocus
          />
          {isSearching && <CircularProgress size={18} sx={{ mr: 1, color: 'text.secondary' }} />}
          {query && !isSearching && (
            <IconButton size="small" onClick={() => setQuery('')} sx={{ opacity: 0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </SearchContainer>

        {/* Results */}
        <ResultsContainer>
          {!hasResults && items.length === 0 && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {displayEmptyMessage}
              </Typography>
            </Box>
          )}

          {!hasResults && items.length > 0 && query && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {displayNoResultsMessage}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  mt: 1,
                  display: 'block',
                }}
              >
                {t('palette.tryDifferent')}
              </Typography>
            </Box>
          )}

          {hasResults && (
            <List ref={listRef} dense disablePadding sx={{ py: 1 }}>
              {/* Recent Items */}
              {showRecent && (
                <>
                  <CategoryHeader>
                    <RecentIcon sx={{ fontSize: iconSizes.sm, opacity: 0.6 }} />
                    <Typography variant="overline" sx={{ fontWeight: 600, opacity: 0.6 }}>
                      {t('palette.recent')}
                    </Typography>
                  </CategoryHeader>
                  {recentItems.slice(0, maxRecentItems).map((item, index) => renderItem(item, index, true))}
                  <Divider sx={{ my: 1 }} />
                </>
              )}

              {/* Grouped Items */}
              {groupedItems.map((group, groupIndex) => (
                <React.Fragment key={group.category?.id ?? 'uncategorized'}>
                  {group.category && (
                    <CategoryHeader>
                      {group.category.icon && (
                        <Box sx={{ display: 'flex', opacity: 0.6, fontSize: iconSizes.sm }}>{group.category.icon}</Box>
                      )}
                      <Typography variant="overline" sx={{ fontWeight: 600, opacity: 0.6 }}>
                        {group.category.label}
                      </Typography>
                    </CategoryHeader>
                  )}
                  {group.items.map((item, itemIndex) => renderItem(item, itemIndex))}
                  {groupIndex < groupedItems.length - 1 && <Divider sx={{ my: 1 }} />}
                </React.Fragment>
              ))}

              {/* "Ask AI" item at the end with visual separator */}
              {askAiItem && (
                <>
                  <Divider sx={{ my: 1 }} />
                  {renderItem(askAiItem, flatItems.length - 1)}
                </>
              )}
            </List>
          )}
        </ResultsContainer>
      </>
    );

    // Handle back to search from chat
    const handleBackToSearch = useCallback(() => {
      setMode('search');
    }, [setMode]);

    // Render chat mode content
    const renderChatContent = () => (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Chat header with back button */}
        <ChatHeader>
          <IconButton size="small" onClick={handleBackToSearch} sx={{ mr: 0.5 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <AiIcon sx={{ fontSize: iconSizes.base, color: 'primary.main' }} />
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
            }}
          >
            {t('palette.mode.chat')}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <KeyboardHint>
            <KeyboardKey>Esc</KeyboardKey>
            <Typography variant="caption">{t('keyboard.back')}</Typography>
          </KeyboardHint>
        </ChatHeader>

        {/* Chat component */}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <CommandPaletteChat
            messages={chatMessages}
            isStreaming={isChatStreaming}
            entityContext={chatContext}
            onSendMessage={onChatSend ?? (() => {})}
            onClearContext={onChatClearContext}
            onClearHistory={onChatClearHistory}
          />
        </Box>
      </Box>
    );

    return (
      <StyledDialog
        open={open}
        onClose={onClose}
        $isDark={isDark}
        $enablePreview={enablePreview}
        $previewWidth={previewWidth}
        onKeyDown={handleKeyDown}
      >
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15 }}
              style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            >
              {/* Mode toggle removed - unified search + chat experience */}

              {/* Two-column layout */}
              <TwoColumnLayout sx={{ flex: 1, minHeight: 0 }}>
                {/* Left column: Search or Chat */}
                <LeftColumn>
                  {currentMode === 'search' ? renderSearchContent() : renderChatContent()}

                  {/* Footer */}
                  {(showKeyboardHints || footer) && currentMode === 'search' && (
                    <FooterContainer>
                      {showKeyboardHints && (
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <KeyboardHint>
                            <KeyboardKey>
                              <ArrowUpIcon
                                sx={{ fontSize: 10 /* Off-scale: keyboard hint icons, smaller than 2xs (12) */ }}
                              />
                            </KeyboardKey>
                            <KeyboardKey>
                              <ArrowDownIcon
                                sx={{ fontSize: 10 /* Off-scale: keyboard hint icons, smaller than 2xs (12) */ }}
                              />
                            </KeyboardKey>
                            <Typography variant="caption">{t('keyboard.navigate')}</Typography>
                          </KeyboardHint>
                          <KeyboardHint>
                            <KeyboardKey>
                              <EnterIcon
                                sx={{ fontSize: 10 /* Off-scale: keyboard hint icons, smaller than 2xs (12) */ }}
                              />
                            </KeyboardKey>
                            <Typography variant="caption">{t('keyboard.select')}</Typography>
                          </KeyboardHint>
                          {/* Show "Ask AI" hint when enabled and AI item is selected */}
                          {enableChat && flatItems[activeIndex]?.id === ASK_AI_ITEM_ID && (
                            <KeyboardHint sx={{ color: 'primary.main' }}>
                              <AiIcon sx={{ fontSize: iconSizes.xs }} />
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {t('palette.askAiHint')}
                              </Typography>
                            </KeyboardHint>
                          )}
                          <KeyboardHint>
                            <KeyboardKey>Esc</KeyboardKey>
                            <Typography variant="caption">{t('keyboard.close')}</Typography>
                          </KeyboardHint>
                        </Box>
                      )}
                      {footer && <Box>{footer}</Box>}
                    </FooterContainer>
                  )}
                </LeftColumn>

                {/* Right column: Preview (only in search mode) */}
                {enablePreview && currentMode === 'search' && (
                  <RightColumn sx={{ width: previewWidth }}>
                    <CommandPalettePreview
                      data={previewData}
                      isLoading={isPreviewLoading}
                      width={previewWidth}
                      onQuickAction={handlePreviewQuickAction}
                      onStartChat={onStartChat}
                      onViewDetails={onViewDetails}
                    />
                  </RightColumn>
                )}
              </TwoColumnLayout>
            </motion.div>
          )}
        </AnimatePresence>
      </StyledDialog>
    );
  },
);

CommandPalette.displayName = 'CommandPalette';

export default CommandPalette;
